import dayjs from "dayjs";

import { useMemo } from "react";

import { FilterOptionsCount, FilterSourceData } from "../typing";
import { AircraftUnknown, MixedAirlineKey } from "../utils/constants";

function count(
  list: FilterSourceData,
  type: "total" | "progress",
  result: FilterOptionsCount
): FilterOptionsCount {
  if (!result["dates"]) {
    result["dates"] = {} as FilterOptionsCount["dates"];
  }
  if (!result["programs"]) {
    result["programs"] = {} as FilterOptionsCount["programs"];
  }
  if (!result["airlines"]) {
    result["airlines"] = {} as FilterOptionsCount["airlines"];
  }
  if (!result["aircrafts"]) {
    result["aircrafts"] = {} as FilterOptionsCount["aircrafts"];
  }
  if (!result["banks"]) {
    result["banks"] = {} as FilterOptionsCount["banks"];
  }
  if (!result["airports"]) {
    result["airports"] = {} as FilterOptionsCount["airports"];
  }
  if (!result["stops"]) {
    result["stops"] = {} as FilterOptionsCount["stops"];
  }

  if (!result["cabins"]) {
    result["cabins"] = {} as FilterOptionsCount["cabins"];
  }
  if (!result["advisory"]) {
    result["advisory"] = {} as FilterOptionsCount["advisory"];
  }
  if (!result["days"]) {
    result["days"] = {} as FilterOptionsCount["days"];
  }

  const dates = result["dates"];
  const programs = result["programs"];
  const airlines = result["airlines"];
  const aircrafts = result["aircrafts"];
  const banks = result["banks"];
  const airports = result["airports"];
  const stops = result["stops"];
  const cabins = result["cabins"];
  const advisory = result["advisory"];
  const days = result["days"];
  for (let index = 0; index < list.length; index++) {
    // dates
    const date = list[index].date;
    if (dates[date]) {
      dates[date][type]++;
    } else {
      dates[date] =
        type === "total"
          ? {
              total: 1,
              progress: 0,
            }
          : {
              total: 0,
              progress: 1,
            };
    }

    // programs
    const programCode = list[index].program_code;

    if (programs[programCode]) {
      programs[programCode][type]++;
    } else {
      programs[programCode] =
        type === "total"
          ? {
              total: 1,
              progress: 0,
            }
          : {
              total: 0,
              progress: 1,
            };
    }

    // cabins
    const cabinType = list[index].payment?.cabin || list[index].final_cabin_type;

    if (cabins[cabinType]) {
      cabins[cabinType][type]++;
    } else {
      cabins[cabinType] =
        type === "total"
          ? {
              total: 1,
              progress: 0,
            }
          : {
              total: 0,
              progress: 1,
            };
    }

    // airlines
    list[index].fare?.products.forEach(({ airline_code }) => {
      let airlineCode = airline_code;

      if (!airlineCode) {
        airlineCode = MixedAirlineKey;
      }

      if (airlines[airlineCode]) {
        airlines[airlineCode][type]++;
      } else {
        airlines[airlineCode] =
          type === "total"
            ? {
                total: 1,
                progress: 0,
              }
            : {
                total: 0,
                progress: 1,
              };
      }
    });

    // aircrafts, 需要先去重，products中可能存在相同的aircraft，应该仅计数一次
    Array.from(new Set(list[index].fare.products.map((item) => item.aircraft))).forEach(
      (aircraft) => {
        aircraft = aircraft || AircraftUnknown;
        if (aircrafts[aircraft]) {
          aircrafts[aircraft][type]++;
        } else {
          aircrafts[aircraft] =
            type === "total"
              ? {
                  total: 1,
                  progress: 0,
                }
              : {
                  total: 0,
                  progress: 1,
                };
        }
      }
    );

    // banks
    const transferOptions = list[index].transfer_options || [];
    transferOptions.forEach(({ program }) => {
      if (banks[program]) {
        banks[program][type]++;
      } else {
        banks[program] =
          type === "total"
            ? {
                total: 1,
                progress: 0,
              }
            : {
                total: 0,
                progress: 1,
              };
      }
    });

    // airports
    list[index].fare.products.forEach(({ origin, destination }) => {
      if (airports[origin]) {
        airports[origin][type]++;
      } else {
        airports[origin] =
          type === "total"
            ? {
                total: 1,
                progress: 0,
              }
            : {
                total: 0,
                progress: 1,
              };
      }

      if (airports[destination]) {
        airports[destination][type]++;
      } else {
        airports[destination] =
          type === "total"
            ? {
                total: 1,
                progress: 0,
              }
            : {
                total: 0,
                progress: 1,
              };
      }
    });

    // advisory
    const advisoryOptions = list[index].advisory || [];
    advisoryOptions.forEach((v) => {
      if (advisory[v]) {
        advisory[v][type]++;
      } else {
        advisory[v] =
          type === "total"
            ? {
                total: 1,
                progress: 0,
              }
            : {
                total: 0,
                progress: 1,
              };
      }
    });

    // stops
    const stopsNum = list[index].fare.products.length - 1;

    for (let key = -1; key < 3; key++) {
      if (!stops[key]) {
        stops[key] = {
          total: 0,
          progress: 0,
        };
      }
    }

    for (let key = stopsNum; key < 3; key++) {
      stops[key][type]++;
    }

    stops[-1][type]++;

    // days
    const day = dayjs(date).day();
    const dayStr = day.toString();
    if (days[dayStr]) {
      days[dayStr][type]++;
    } else {
      days[dayStr] =
        type === "total"
          ? {
              total: 1,
              progress: 0,
            }
          : {
              total: 0,
              progress: 1,
            };
    }
  }

  return result;
}

export const useFilterOptionsCount = (
  list: FilterSourceData,
  data: FilterSourceData
): FilterOptionsCount => {
  return useMemo(() => {
    const result = {} as FilterOptionsCount;
    count(list, "total", result);
    count(data, "progress", result);

    return result;
  }, [list, data]);
};
